import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type UploadedData = {
  fileName: string;
  columns: string[];
  rows: Record<string, unknown>[];
  totalRows: number;
};

function parseCSV(text: string): { columns: string[]; rows: Record<string, unknown>[] } {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length === 0) return { columns: [], rows: [] };

  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const rows = lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      const val = values[i] ?? '';
      const num = Number(val);
      row[h] = val === '' ? null : !isNaN(num) && val !== '' ? num : val;
    });
    return row;
  });

  return { columns: headers, rows };
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-data-assistant`;

export function useAIDataAssistant() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedData, setUploadedData] = useState<UploadedData | null>(null);

  const handleFileUpload = useCallback(async (file: File) => {
    const text = await file.text();
    const { columns, rows } = parseCSV(text);

    if (columns.length === 0) {
      toast.error('Could not parse the file. Please upload a valid CSV.');
      return;
    }

    const data: UploadedData = {
      fileName: file.name,
      columns,
      rows: rows.slice(0, 20), // sample for AI
      totalRows: rows.length,
    };
    setUploadedData(data);

    // Auto-send analysis message
    const userMsg = `I've uploaded a file called "${file.name}" with ${rows.length} rows and ${columns.length} columns.\n\nColumns: ${columns.join(', ')}\n\nHere are the first few rows:\n\`\`\`json\n${JSON.stringify(rows.slice(0, 5), null, 2)}\n\`\`\`\n\nPlease analyze this data and tell me what you see.`;
    
    await sendMessage(userMsg, []);
  }, []);

  const sendMessage = useCallback(async (input: string, prevMessages?: ChatMessage[]) => {
    const history = prevMessages ?? messages;
    const userMsg: ChatMessage = { role: 'user', content: input };
    const updatedMessages = [...history, userMsg];
    setMessages(updatedMessages);
    setIsLoading(true);

    let assistantContent = '';

    try {
      // The assistant endpoint validates the signed-in user's session, so the
      // request must carry their access token (not the app's public key).
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        toast.error('Your session has expired. Please sign in again to ask questions.');
        setIsLoading(false);
        return;
      }

      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ messages: updatedMessages }),
      });

      if (resp.status === 401) {
        toast.error('Your session has expired. Please sign in again to ask questions.');
        setIsLoading(false);
        return;
      }

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Request failed' }));
        toast.error(err.error || 'AI request failed');
        setIsLoading(false);
        return;
      }

      if (!resp.body) {
        toast.error('No response stream');
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantContent += delta;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantContent } : m);
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
              });
            }
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // Flush remaining buffer
      if (buffer.trim()) {
        for (let raw of buffer.split('\n')) {
          if (!raw || !raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantContent += delta;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantContent } : m);
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      console.error('Stream error:', e);
      toast.error('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [messages]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setUploadedData(null);
  }, []);

  return {
    messages,
    isLoading,
    uploadedData,
    handleFileUpload,
    sendMessage,
    clearChat,
  };
}
